"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.apiDeleteFileFromStudentNote = exports.apiAddFileToStudentNote = exports.apiDeleteFileFromCourseNote = exports.apiAddFileToCourseNote = exports.apiDeleteFileFromNote = exports.apiAddFileToNote = exports.isDraftNote = exports.apiGetCourseNotesForStudents = exports.apiGetStudentNotesByElementIds = exports.apiGetStudentNotes = exports.apiGetCourseNotesByElementIds = exports.apiGetCourseNotes = exports.apiDeleteCourseNote = exports.apiUpdateCourseNote = exports.apiAddCourseNote = exports.apiDeleteStudentNote = exports.apiUpdateStudentNote = exports.apiAddStudentNote = exports.apiDeleteNote = exports.apiUpdateNote = exports.apiAddNote = void 0;
const client_1 = require("./client");
const util_1 = require("./util");
/**
 * Add a note.
 *
 * The formdata must include:
 * - `subject`
 * - `body`
 * - `exerciseId`
 * - `noteFiles`
 * - `isPublished` (if teacher)
 *
 * Whether a student note or course note will be created will
 * be determined by the currently signed-in user.
 * @param formData formdata containing the data.
 * @param courseId required when the user is a teacher.
 * @returns the newly created student or course note.
 */
const apiAddNote = async (formData, courseId) => {
    validateFormDataForNoteCreation(formData);
    if ((0, util_1.isStudentUser)()) {
        return (0, exports.apiAddStudentNote)(formData);
    }
    if (!courseId) {
        throw new Error('Parameter "courseId" is missing in "apiAddNote" but it is required due to the user being a teacher');
    }
    if (!formData.has('isPublished')) {
        throw new Error('FormData field "isPublished" is missing but it is required due to the user being a teacher');
    }
    return (0, exports.apiAddCourseNote)(formData, courseId);
};
exports.apiAddNote = apiAddNote;
/**
 * Update a note.
 *
 * The formdata can include:
 * - `subject`
 * - `body`
 * - `fileHashesToDelete: string[]`
 * - `noteFiles`
 * - `isPublished` (if teacher)
 *
 * Whether a student note or course note will be created will
 * be determined by the currently signed-in user.
 * @param formData
 * @param noteId note to update
 * @param courseId required when the user is a teacher.
 * @returns the newly created student or course note.
 */
const apiUpdateNote = async (formData, noteId, courseId) => {
    if ((0, util_1.isStudentUser)()) {
        return (0, exports.apiUpdateStudentNote)(formData, noteId);
    }
    if (!courseId) {
        throw new Error('Parameter "courseId" is missing in "apiAddNote" but it is required due to the user being a teacher');
    }
    if (!formData.has('isPublished')) {
        throw new Error('FormData field "isPublished" is missing but it is required due to the user being a teacher');
    }
    return (0, exports.apiUpdateCourseNote)(formData, courseId, noteId);
};
exports.apiUpdateNote = apiUpdateNote;
/**
 * Delete the given note.
 *
 * Whether a student note or course note will be deleted will
 * be determined by the currently signed-in user.
 * @param noteId the note to delete
 */
const apiDeleteNote = async (noteId) => {
    if ((0, util_1.isStudentUser)()) {
        await (0, exports.apiDeleteStudentNote)(noteId);
    }
    else {
        await (0, exports.apiDeleteCourseNote)(noteId);
    }
};
exports.apiDeleteNote = apiDeleteNote;
/**
 * Add a student note.
 *
 * Formdata fields:
 * - `subject`
 * - `body`
 * - `exerciseId`
 * - `noteFiles`
 * @param formData
 * @returns the newly created note
 */
const apiAddStudentNote = async (formData) => {
    validateFormDataForNoteCreation(formData);
    const { data } = await client_1.default.post('/notes/student/add-note', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return data;
};
exports.apiAddStudentNote = apiAddStudentNote;
/**
 * Update a student note
 * Formdata fields:
 * - `subject`
 * - `body`
 * - `exerciseId`
 * - `noteFiles`
 * - `fileHashesToDelete`
 * @param formData
 * @param noteId note to update
 * @returns the updated note
 */
const apiUpdateStudentNote = async (formData, noteId) => {
    const { data } = await client_1.default.put(`/notes/student/update-note/${noteId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return data;
};
exports.apiUpdateStudentNote = apiUpdateStudentNote;
/**
 * Delete the note of a student.
 * @param noteId note to delete
 */
const apiDeleteStudentNote = async (noteId) => {
    await client_1.default.delete(`/notes/student/delete-note/${noteId}`);
};
exports.apiDeleteStudentNote = apiDeleteStudentNote;
/**
 * Formdata fields:
 * - `subject`
 * - `body`
 * - `exerciseId`
 * - `isPublished`
 * - `noteFiles`
 * @param formData
 * @param courseId
 * @returns the newly created note
 */
const apiAddCourseNote = async (formData, courseId) => {
    validateFormDataForNoteCreation(formData);
    const { data } = await client_1.default.post(`/notes/course/add-note/${courseId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return data;
};
exports.apiAddCourseNote = apiAddCourseNote;
/**
 * Update a course note.
 * FormData fields:
 * - `subject`
 * - `body`
 * - `exerciseId`
 * - `isPublished`
 * - `noteFiles`
 * - `fileHashesToDelete`
 * @param formData
 * @param courseId course of the note
 * @param noteId  note to update
 * @returns the updated note
 */
const apiUpdateCourseNote = async (formData, courseId, noteId) => {
    const { data } = await client_1.default.put(`/notes/course/update-note/${courseId}/${noteId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return data;
};
exports.apiUpdateCourseNote = apiUpdateCourseNote;
/**
 * Delete a course note
 * @param noteId note to delete
 */
const apiDeleteCourseNote = async (noteId) => {
    await client_1.default.delete(`/notes/course/delete-note/${noteId}`);
};
exports.apiDeleteCourseNote = apiDeleteCourseNote;
/**
 * Get the notes of a course.
 * @param courseId
 * @param after optional after date
 * @param excludeDeleted
 * @returns the notes of the course.
 */
const apiGetCourseNotes = async (courseId, after, excludeDeleted = false) => {
    const { data } = await client_1.default.get(`/notes/course/get-course-notes/${courseId}`, {
        params: Object.assign({ excludeDeleted }, (after && { after: after.getTime() })),
    });
    return data;
};
exports.apiGetCourseNotes = apiGetCourseNotes;
/**
 * Get the course notes for the given element ids.
 * @param courseId
 * @param elementIds
 * @param after optional after date
 * @param excludeDeleted
 * @returns notes for the element ids
 */
const apiGetCourseNotesByElementIds = async (courseId, elementIds, after, excludeDeleted = false) => {
    const { data } = await client_1.default.post(`/notes/course/by-element-ids/${courseId}`, {
        elementIds,
    }, {
        params: Object.assign({ excludeDeleted }, (after && { after: after.getTime() })),
    });
    return data;
};
exports.apiGetCourseNotesByElementIds = apiGetCourseNotesByElementIds;
/**
 * Get the notes of a student.
 * @param after optional after date
 * @param excludeDeleted
 * @returns the notes of the student.
 */
const apiGetStudentNotes = async (after, excludeDeleted = false) => {
    const { data } = await client_1.default.get('/notes/student/get-student-notes', {
        params: Object.assign({ excludeDeleted }, (after && { after: after.getTime() })),
    });
    return data;
};
exports.apiGetStudentNotes = apiGetStudentNotes;
/**
 * Get the student notes for the given element ids.
 * @param elementIds
 * @param after optional after date
 * @param excludeDeleted
 * @returns the student notes for the given element ids.
 */
const apiGetStudentNotesByElementIds = async (elementIds, after, excludeDeleted = false) => {
    const { data } = await client_1.default.post('/notes/student/by-element-ids', {
        elementIds,
    }, {
        params: Object.assign({ excludeDeleted }, (after && { after: after.getTime() })),
    });
    return data;
};
exports.apiGetStudentNotesByElementIds = apiGetStudentNotesByElementIds;
/**
 * Get the course notes for a student.
 *
 * This will only fetch published notes.
 * @param after optional after timestamp
 * @param excludeDeleted optional flag to exclude deleted messages
 * @returns course notes for the student
 */
const apiGetCourseNotesForStudents = async (after, excludeDeleted = false) => {
    const { data } = await client_1.default.get('/notes/course/get-course-notes-for-student', {
        params: Object.assign({ excludeDeleted }, (after && { after: after.getTime() })),
    });
    return data;
};
exports.apiGetCourseNotesForStudents = apiGetCourseNotesForStudents;
const isDraftNote = (n) => n.subject === undefined;
exports.isDraftNote = isDraftNote;
/**
 * Add a single file from a note.
 * @param formData formdata containing the file
 * @param noteId the note to add to
 * @param courseId **required when user is a teacher**
 */
const apiAddFileToNote = async (formData, noteId, courseId) => {
    validateFormDataForSingleUpload(formData);
    if ((0, util_1.isStudentUser)()) {
        return (0, exports.apiAddFileToStudentNote)(noteId, formData);
    }
    if (!courseId) {
        throw new Error('\'courseId\' is required for adding file to a course note');
    }
    return (0, exports.apiAddFileToCourseNote)(courseId, noteId, formData);
};
exports.apiAddFileToNote = apiAddFileToNote;
/**
 * Delete a single file from a note.
 * @param noteId the note to add to
 * @param fileId the file to delete
 * @param courseId **required when user is a teacher**
 */
const apiDeleteFileFromNote = async (noteId, fileId, courseId) => {
    if ((0, util_1.isStudentUser)()) {
        return (0, exports.apiDeleteFileFromStudentNote)(noteId, fileId);
    }
    if (!courseId) {
        throw new Error('\'courseId\' is required for deleting a file from a course note');
    }
    return (0, exports.apiDeleteFileFromCourseNote)(courseId, noteId, fileId);
};
exports.apiDeleteFileFromNote = apiDeleteFileFromNote;
/**
 * Add a single file to a course note.
 *
 * The file must be under the key `file` within the formdata.
 *
 * If the formdata doesn't have a key `file`, an error will be thrown.
 * @param courseId The course which owns the note
 * @param noteId The note to add the file to
 * @param formData formdata containing the new file
 * @return the updated note
 */
const apiAddFileToCourseNote = async (courseId, noteId, formData) => {
    validateFormDataForSingleUpload(formData);
    const { data } = await client_1.default.post(`/notes/course/add-file/${courseId}/${noteId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return data;
};
exports.apiAddFileToCourseNote = apiAddFileToCourseNote;
/**
 * Delete a file from the course note.
 *
 * @param courseId The course which owns the note
 * @param noteId The note to add the file to
 * @param fileId the file to delete
 * @return the updated note
 */
const apiDeleteFileFromCourseNote = async (courseId, noteId, fileId) => {
    const { data } = await client_1.default.delete(`/notes/course/delete-file/${courseId}/${noteId}/${fileId}`);
    return data;
};
exports.apiDeleteFileFromCourseNote = apiDeleteFileFromCourseNote;
/**
 * Add a single file to a student note.
 *
 * The file must be under the key `file` within the formdata.
 *
 * If the formdata doesn't have a key `file`, an error will be thrown.
 * @param noteId The note to add the file to
 * @param formData formdata containing the new file
 * @return the updated note
 */
const apiAddFileToStudentNote = async (noteId, formData) => {
    validateFormDataForSingleUpload(formData);
    const { data } = await client_1.default.post(`/notes/student/add-file/${noteId}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return data;
};
exports.apiAddFileToStudentNote = apiAddFileToStudentNote;
/**
 * Delete a file from the student note.
 *
 * @param noteId The note to remove the file from
 * @param fileId the file to delete
 * @return the updated note
 */
const apiDeleteFileFromStudentNote = async (noteId, fileId) => {
    const { data } = await client_1.default.delete(`/notes/student/delete-file/${noteId}/${fileId}`);
    return data;
};
exports.apiDeleteFileFromStudentNote = apiDeleteFileFromStudentNote;
const validateFormDataForSingleUpload = (formData) => {
    if (!formData.has('file')) {
        throw new Error('FormData is missing field: "file"');
    }
};
const validateFormDataForNoteCreation = (formData) => {
    if (!formData.has('subject')) {
        throw new Error('FormData is missing field: "subject"');
    }
    if (!formData.has('body')) {
        throw new Error('FormData is missing field: "body"');
    }
    if (!formData.has('exerciseId')) {
        throw new Error('FormData is missing field: "exerciseId"');
    }
};
//# sourceMappingURL=notes.js.map